// "Here is what changed" after a companion live refresh
// (ai.companion_panel_v2). Shared by both refresh paths: the Inertia
// bridge (@shared/lib/companion-refresh, router.reload) and the Hotwire
// fallback (companion_controller.js#flushRecordChanged, a Turbo morph).
//
// Text-based, not record-based: the page behind the panel is any page, so
// the only thing both renderers have in common is the DOM. Snapshot the text
// of every leaf element before the refresh, compare after, and flash the
// leaves whose text differs. A node that did not exist before (a keyed
// component remounted) only counts when its text is NEW to the page, so a
// remount alone never lights up content that did not change.

export interface TextSnapshot {
  byElement: WeakMap<Element, string>
  texts: Set<string>
}

// A burst of changes (a whole table re-sorted) is not something a flash can
// point at; past this many only the first ones flash.
const MAX_HIGHLIGHTS = 12
// The companion panel itself never counts: it streams its own messages, and
// on Hotwire pages it lives inside the root being compared.
const SKIP =
  "script, style, template, svg, canvas, noscript, #ai-companion-panel"

const FLASH_KEYFRAMES: Keyframe[] = [
  {
    backgroundColor: "rgb(59 191 198 / 0.28)",
    boxShadow: "0 0 0 3px rgb(59 191 198 / 0.28)",
  },
  { backgroundColor: "transparent", boxShadow: "0 0 0 3px transparent" },
]

const textOf = (el: Element) => (el.textContent ?? "").trim()

function leaves(root: Element): Element[] {
  return [...root.querySelectorAll("*")].filter(
    (el) =>
      el.childElementCount === 0 && !el.closest(SKIP) && textOf(el) !== "",
  )
}

const reducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

export function snapshotTexts(root: Element): TextSnapshot {
  const byElement = new WeakMap<Element, string>()
  const texts = new Set<string>()
  for (const el of leaves(root)) {
    const text = textOf(el)
    byElement.set(el, text)
    texts.add(text)
  }
  return { byElement, texts }
}

// Returns how many elements it flashed.
export function highlightChanges(root: Element, before: TextSnapshot): number {
  if (reducedMotion()) return 0

  const changed = leaves(root).filter((el) => {
    const text = textOf(el)
    const previous = before.byElement.get(el)
    return previous === undefined ? !before.texts.has(text) : previous !== text
  })

  const flashed = changed.slice(0, MAX_HIGHLIGHTS)
  for (const el of flashed) {
    if (typeof (el as HTMLElement).animate !== "function") continue
    ;(el as HTMLElement).animate(FLASH_KEYFRAMES, {
      duration: 1800,
      easing: "ease-out",
    })
  }
  return flashed.length
}
